// =============================================================================
// Expansion CRM — HTTP client (chama o Cloudflare Worker)
// =============================================================================

const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export interface ApiErrorPayload {
  error?: string;
  code?: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  headers?: Record<string, string>;
  raw?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, raw = false, signal } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  let bodyInit: BodyInit | undefined;

  if (body !== undefined) {
    bodyInit = JSON.stringify(body);
    finalHeaders['Content-Type'] = 'application/json';
  }

  const url = path.startsWith('http') ? path : `${API_URL}${path}`;

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: bodyInit,
    credentials: 'include',
    signal,
  });

  if (raw) {
    if (!response.ok) {
      throw new ApiError(response.status, await extractError(response));
    }
    return response as unknown as T;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      (isJson && ((payload as ApiErrorPayload).error ?? (payload as ApiErrorPayload).message)) ||
      (typeof payload === 'string' && payload) ||
      `Erro ${response.status}`;
    const code = isJson ? (payload as ApiErrorPayload).code : undefined;
    throw new ApiError(response.status, message, code);
  }

  return payload as T;
}

async function extractError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return (body as ApiErrorPayload).error ?? `Erro ${response.status}`;
  } catch {
    return `Erro ${response.status}`;
  }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}
